/**
 * Team groups: sets of teams of one type. A user given a role on a team group
 * holds that role on every team in it. The group names its teams; the users
 * are held on each user's roles.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface TeamGroupTeam {
  id: string
  name: string
  type?: string
  objectState?: string
}

export interface TeamGroup {
  id: string
  name: string
  description: string
  type: string
  objectState: string
  labels: string[]
  teams: TeamGroupTeam[]
  validationWarnings: ValidationWarning[]
  createTime: string
  updateTime: string
}

export interface TeamGroupInput {
  name: string
  description?: string
  type?: string
  labels?: string[]
  /** The ids of the teams in the group; replaces the whole set when given. */
  teams?: string[]
  objectState?: string
}

function mapTeamGroup(r: Record<string, unknown>): TeamGroup {
  // Teams come back as references, but tolerate bare ids as the input takes.
  const teams = ((r.teams as unknown[]) ?? []).map((team) =>
    typeof team === 'string'
      ? { id: team, name: team }
      : {
          id: ((team as Record<string, unknown>).id as string) ?? '',
          name: ((team as Record<string, unknown>).name as string) ?? '',
          type: (team as Record<string, unknown>).type as string | undefined,
          objectState: (team as Record<string, unknown>).objectState as string | undefined,
        }
  )
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    type: (r.type as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    labels: (r.labels as string[]) ?? [],
    teams,
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
  }
}

/** The options a team group list page is narrowed by on the server. */
export interface TeamGroupListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of team groups, narrowed by search and state on the server. */
async function fetchTeamGroupPage(
  search: string | undefined,
  status: ObjectStatus | undefined,
  page: number,
  pageSize: number,
  sort?: ListSort | null
): Promise<ListPage<TeamGroup>> {
  const params = new URLSearchParams({
    ...searchParams('teamGroups', search),
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/teamgroups?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch team groups')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapTeamGroup)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const TeamGroupsApi = {
  /** One page of team groups, filtered by search and state on the server. */
  getPage: ({ page = 1, pageSize = 25, search, status, sort }: TeamGroupListOptions = {}) =>
    fetchTeamGroupPage(search, status, page, pageSize, sort),

  /** Every team group matching the filters, walked page by page — for downloads. */
  getAllPages: (search?: string, status?: ObjectStatus): Promise<TeamGroup[]> =>
    fetchAllPages((page, pageSize) => fetchTeamGroupPage(search, status, page, pageSize)),

  /**
   * Puts a team in a group, taking it out of any other group it was in. With
   * `removeOnly` the team is just taken out of this group.
   */
  setTeam: async (id: string, teamId: string, removeOnly = false): Promise<void> => {
    const params = new URLSearchParams({ context: getContext(), removeOnly: String(removeOnly) })
    const res = await apiFetch(
      `${API_BASE}/teamgroups/${encodeURIComponent(id)}/${encodeURIComponent(teamId)}?${params}`,
      { method: 'POST', headers: JSON_HEADERS, body: '{}' }
    )
    if (!res.ok) await throwApiError(res, 'Failed to update team group')
  },

  getById: async (id: string): Promise<TeamGroup> => {
    const res = await apiFetch(`${API_BASE}/teamgroups/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch team group')
    return mapTeamGroup(await res.json())
  },

  create: async (id: string, input: TeamGroupInput): Promise<TeamGroup> => {
    const res = await apiFetch(`${API_BASE}/teamgroups/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create team group')
    return mapTeamGroup(await res.json())
  },

  update: async (id: string, input: TeamGroupInput): Promise<TeamGroup> => {
    const res = await apiFetch(`${API_BASE}/teamgroups/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save team group')
    return mapTeamGroup(await res.json())
  },
}
